import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { LineChart, Compass, FileCode, FileText, SlidersHorizontal, Code2, TrendingUp } from "lucide-react";
interface Workstream {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const workstreams: Workstream[] = [{
  id: "data-led-strategy",
  title: "Data-led strategy",
  description: "I analyse Google Search Console, website analytics and your internal commercial data so every strategic SEO decision is anchored in real business objectives.",
  icon: LineChart
}, {
  id: "commercial-keyword-prioritisation",
  title: "Commercial keyword prioritisation",
  description: "I pinpoint search queries that generate genuine enquiries and sales, mapping relevant high-intent phrases to target pages based on user search intent.",
  icon: Compass
}, {
  id: "technical-foundations",
  title: "Robust technical foundations",
  description: "I resolve crawling barriers, indexation issues, canonical tags and internal architecture so search engine bots can discover and evaluate priority pages efficiently.",
  icon: FileCode
}, {
  id: "content-and-authority",
  title: "Helpful content and authority",
  description: "I refine on-page copy, improve search depth and pursue credible, relevant links to build sustainable topical trust without artificial schemes.",
  icon: FileText
}, {
  id: "impact-effort-planning",
  title: "Impact-versus-effort planning",
  description: "Every recommended task is ranked by expected return versus resource requirement, making efficient use of your developer and content resources.",
  icon: SlidersHorizontal
}, {
  id: "implementation-support",
  title: "Implementation support",
  description: "I provide clear written briefs for your developers and writers, explain recommendations in plain English, and make direct CMS updates where scope allows.",
  icon: Code2
}, {
  id: "conversion-focused-reporting",
  title: "Conversion-focused reporting",
  description: "Monthly reports track organic visibility, qualified traffic and actual enquiries or sales, rather than arbitrary lists of keyword ranking positions.",
  icon: TrendingUp
}];
export default function Approach() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="approach" aria-labelledby="approach-heading" ref={ref} className="bg-background py-20 lg:py-28 border-b border-border transition-opacity duration-700 ease-out">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`max-w-3xl mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="text-xs font-semibold tracking-wider uppercase text-primary mb-3">
            Strategic Methodology
          </p>
          <h2 id="approach-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            How I Approach SEO Growth
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every website presents unique market conditions and technical challenges.
            I approach sustainable organic growth through seven practical disciplines,
            focusing resources on the initiatives that deliver the highest commercial return.
          </p>
        </div>

        {/* Workstreams Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {workstreams.map((item, index) => {
          const Icon = item.icon;
          return <div key={item.id} className={`bg-card text-card-foreground border border-border rounded-xl p-6 sm:p-7 flex flex-col justify-between transition-all duration-500 hover:border-primary/40 hover:shadow-sm ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: `${index * 75}ms`
          }}>
                <div>
                  <div className="w-11 h-11 rounded-lg bg-secondary/60 text-primary flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 stroke-[1.75]" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-3 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {/* Scope and Priorities Note */}
        <div className={`mt-12 p-6 sm:p-7 rounded-xl bg-muted/40 border border-border transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              <strong className="text-foreground font-semibold">
                Please note:
              </strong>{" "}
              The specific scope, sequencing, and priorities of these workstreams
              depend entirely on the initial audit findings, your commercial goals,
              and the written agreement made before work starts.
            </p>
          </div>
        </div>
      </div>
    </section>;
}